import { useScreenSize } from '@/hooks/useScreenSize';
import IconArrow from '~/icons/arrow-right.svg';
import { IconButton } from '../../atoms/IconButton';

type PaginationProps = {
  label: string;
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

function getPageNumbers(current: number, total: number, max: number = 7) {
  const pages: (number | 'ellipsis')[] = [];

  if (total <= max) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  const showPages = 1;
  const window = 1;

  if (current === 1) {
    for (let i = 1; i <= 4 && i <= total; i++) pages.push(i);
    if (total > 5) pages.push('ellipsis');
    if (total > 4) {
      for (let i = total - showPages + 1; i <= total; i++) {
        if (i > 4) pages.push(i);
      }
    }
    return pages;
  }

  for (let i = 1; i <= showPages; i++) pages.push(i);

  if (current > showPages + window + 1) pages.push('ellipsis');

  const start = Math.max(current - window, showPages + 1);
  const end = Math.min(current + window, total - showPages);

  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - showPages - window) pages.push('ellipsis');

  for (let i = total - showPages + 1; i <= total; i++) {
    if (i > showPages && i > end) pages.push(i);
  }

  return pages;
}
export function Pagination({
  label,
  loading,
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const { isDesktop } = useScreenSize();

  const pages = getPageNumbers(currentPage, totalPages, 7);

  return (
    <nav aria-label='Pagination' className='flex items-center w-full'>
      <div
        className={!isDesktop ? 'flex justify-between w-full' : 'flex gap-2'}
      >
        <div>
          {(isDesktop ? currentPage > 1 : true) && (
            <IconButton
              onClick={() => onPageChange(currentPage - 1)}
              disabled={loading || currentPage === 1}
              variant={!isDesktop ? 'outlined' : 'text'}
              color='primary'
            >
              <IconArrow className='rotate-180' width={16} height={16} />
            </IconButton>
          )}
        </div>
        <div>
          {isDesktop && (
            <div className='gap-2 flex'>
              {pages.map((page, idx) =>
                page === 'ellipsis' ? (
                  <span className='text-gray-500' key={`ellipsis-${idx}`}>
                    …
                  </span>
                ) : (
                  <IconButton
                    key={page}
                    onClick={() => onPageChange(Number(page))}
                    disabled={loading || page === currentPage}
                    variant={page === currentPage ? 'outlined' : 'text'}
                    color='gray'
                  >
                    {page}
                  </IconButton>
                )
              )}
            </div>
          )}
        </div>
        <div>
          {(isDesktop ? currentPage < totalPages : true) && (
            <IconButton
              onClick={() => onPageChange(currentPage + 1)}
              disabled={loading || currentPage === totalPages}
              variant={!isDesktop ? 'outlined' : 'text'}
              color='primary'
            >
              <IconArrow width={16} height={16} />
            </IconButton>
          )}
        </div>
      </div>
    </nav>
  );
}
